import {
  useState,
  useEffect,
  type ChangeEvent,
  type HTMLAttributes,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Save as SaveIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import axios from "axios";
import api from "../../api";

interface SiteContent {
  about_title_en: string;
  about_title_ro: string;
  about_content_en: string;
  about_content_ro: string;

  contact_title_en: string;
  contact_title_ro: string;
  contact_content_en: string;
  contact_content_ro: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_hours: string;

  studio_title_en: string;
  studio_title_ro: string;
  studio_content_en: string;
  studio_content_ro: string;
}

interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  value: number;
  index: number;
  isMobile: boolean;
}

function TabPanel({
  children,
  value,
  index,
  isMobile,
  ...other
}: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: isMobile ? 2 : 3 }}>{children}</Box>}
    </div>
  );
}

function ContentManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState<SiteContent>({
    about_title_en: "",
    about_title_ro: "",
    about_content_en: "",
    about_content_ro: "",

    contact_title_en: "",
    contact_title_ro: "",
    contact_content_en: "",
    contact_content_ro: "",
    contact_email: "",
    contact_phone: "",
    contact_address: "",
    contact_hours: "",

    studio_title_en: "",
    studio_title_ro: "",
    studio_content_en: "",
    studio_content_ro: "",
  });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/content/admin");

      setFormData(response.data);
    } catch (error) {
      console.error("Error loading content:", error);
      setError("Failed to load content. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: keyof SiteContent, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveContent = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await api.put("/content/admin", formData);

      setSuccess("Site content saved successfully!");
    } catch (error) {
      console.error("Error saving site content:", error);
      setError("Failed to save site content. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError("");
    setSuccess("");
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 3 }}
    >
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          gutterBottom
        >
          Content Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage the content for your About, Contact & Studio pages.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, fontSize: isMobile ? "0.875rem" : "1rem" }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2, fontSize: isMobile ? "0.875rem" : "1rem" }}
        >
          {success}
        </Alert>
      )}

      <Paper elevation={isMobile ? 1 : 3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="content management tabs"
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTab-root": {
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 48 : 56,
                padding: isMobile ? "8px 12px" : "12px 16px",
              },
            }}
          >
            <Tab label="About Page" />
            <Tab label="Contact Page" />
            <Tab label="Studio Page" />
          </Tabs>
        </Box>

        {/* About Page */}
        <TabPanel value={activeTab} index={0} isMobile={isMobile}>
          <Box sx={{ mb: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              About Page Content
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: isMobile ? 2 : 3 }}
            >
              Edit the title and content that appears on your About page.
            </Typography>
          </Box>

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* English column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Title (English)"
                value={formData.about_title_en}
                onChange={(event) =>
                  handleFieldChange("about_title_en", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page Content (English)"
                value={formData.about_content_en}
                onChange={(event) =>
                  handleFieldChange("about_content_en", event.target.value)
                }
                multiline
                rows={4}
                required
              />
            </Grid>

            {/* Romanian column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Title (Romanian)"
                value={formData.about_title_ro}
                onChange={(event) =>
                  handleFieldChange("about_content_ro", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page Content (Romanian)"
                value={formData.about_content_ro}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    about_content_ro: event.target.value,
                  })
                }
                multiline
                rows={4}
                required
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: isMobile ? 2 : 3,
              display: "flex",
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              {saving ? "Saving..." : "Save About Page"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              Refresh
            </Button>
          </Box>
        </TabPanel>

        {/* Contact Page */}
        <TabPanel value={activeTab} index={1} isMobile={isMobile}>
          <Box sx={{ mb: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Contact Page Content
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: isMobile ? 2 : 3 }}
            >
              Edit the content and contact information that appears on your
              Contact page.
            </Typography>
          </Box>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* English column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Title (English)"
                value={formData.contact_title_en}
                onChange={(event) =>
                  handleFieldChange("contact_title_en", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page Content (English)"
                value={formData.contact_content_en}
                onChange={(event) =>
                  handleFieldChange("contact_content_en", event.target.value)
                }
                multiline
                rows={4}
                required
              />
            </Grid>

            {/* Romanian column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Title (Romanian)"
                value={formData.contact_title_ro}
                onChange={(event) =>
                  handleFieldChange("contact_title_ro", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page Content (Romanian)"
                value={formData.contact_content_ro}
                onChange={(event) =>
                  handleFieldChange("contact_content_ro", event.target.value)
                }
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                value={formData.contact_email}
                onChange={(event) =>
                  handleFieldChange("contact_email", event.target.value)
                }
                variant="outlined"
                type="email"
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.875rem" : "1rem",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.contact_phone}
                onChange={(event) =>
                  handleFieldChange("contact_phone", event.target.value)
                }
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.875rem" : "1rem",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Address"
                value={formData.contact_address}
                onChange={(event) =>
                  handleFieldChange("contact_address", event.target.value)
                }
                variant="outlined"
                placeholder="Enter your business address..."
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.875rem" : "1rem",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label="Business Hours"
                value={formData.contact_hours}
                onChange={(event) =>
                  handleFieldChange("contact_hours", event.target.value)
                }
                variant="outlined"
                placeholder="Enter your business hours..."
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.875rem" : "1rem",
                  },
                }}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: isMobile ? 2 : 3,
              display: "flex",
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              {saving ? "Saving..." : "Save Contact Page"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              Refresh
            </Button>
          </Box>
        </TabPanel>

        {/*Studio Page */}
        <TabPanel value={activeTab} index={2} isMobile={isMobile}>
          <Box sx={{ mb: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Studio Page Content
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: isMobile ? 2 : 3 }}
            >
              Edit the title and content that appears on your Studio section on
              the Home Page.
            </Typography>
          </Box>

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* English column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Studio Title (English)"
                value={formData.studio_title_en}
                onChange={(event) =>
                  handleFieldChange("studio_title_en", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Page Content (English)"
                value={formData.studio_content_en}
                onChange={(event) =>
                  handleFieldChange("studio_content_en", event.target.value)
                }
                multiline
                rows={4}
                required
              />
            </Grid>

            {/* Romanian column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Studio Title (Romanian)"
                value={formData.studio_title_ro}
                onChange={(event) =>
                  handleFieldChange("studio_title_ro", event.target.value)
                }
                required
              />

              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Studio Content (Romanian)"
                value={formData.studio_content_ro}
                onChange={(event) =>
                  handleFieldChange("studio_content_ro", event.target.value)
                }
                multiline
                rows={4}
                required
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: isMobile ? 2 : 3,
              display: "flex",
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              {saving ? "Saving..." : "Save Studio Page"}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadContent}
              disabled={saving}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
              sx={{
                fontSize: isMobile ? "0.875rem" : "1rem",
                minHeight: isMobile ? 40 : 48,
              }}
            >
              Refresh
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default ContentManagement;
